import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import UserMenu from './UserMenu.jsx';
import ViewAsBanner from './ViewAsBanner.jsx';
import NavMore from './NavMore.jsx';
import HorseHeadIcon from '../icons/HorseHeadIcon.jsx';
import logoUrl from '../../assets/stallside-logo.png';

// Unified nav: the places you go daily as tabs, everything else under More.
// Forest (#1a2b1f) bar with cream (#f5f1e8) labels, identical on desktop and
// mobile. Account / View-as / Log out remain in the top-right UserMenu.
//
// The tabs are role-shaped rather than universal, which is what keeps the bar
// from crowding: a trainer gets Availability and a groom gets Shifts, and only
// an owner or manager sees both. Nobody carries an icon for a screen their role
// never opens.
//
// More is grouped by what a thing is FOR, not by whether it counts as a
// setting. Shift types belong under Shifts: they are the vocabulary the roster
// is written in, and filing them in a drawer marked Settings means remembering
// where they were put rather than thinking about what they do.
//
// The sections are the caller's order — NavMore starts a new group whenever the
// section changes, so items that belong together have to stay together here.
export default function AppShell() {
  const { user, barns, activeRole } = useAuth();
  const activeBarn = barns?.find((b) => b.id === user?.activeBarnId);
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const canSchedule = ['owner', 'manager', 'trainer'].includes(activeRole);
  const isStaff = ['owner', 'manager', 'trainer', 'groom'].includes(activeRole);

  // Secondary destinations, role-filtered, under "More".
  // Booking is shown to everyone: clients claim slots, staff book on someone's
  // behalf. It leads because for a client it is the ONLY item here — without it
  // they never see a More menu at all.
  const moreItems = [
    // Booking leads, and carries no heading: for a client it is the only row in
    // here, and a lone item under a section label reads as a category with one
    // thing in it rather than the thing itself.
    { to: '/schedule/book', label: 'Book a lesson', show: true },

    { section: 'Shifts', to: '/settings/routines', label: 'Routines', show: canManage },
    { section: 'Shifts', to: '/settings/shift-types', label: 'Shift types', show: canManage },

    { section: 'Lessons', to: '/settings/lesson-types', label: 'Lesson types', show: canManage },

    // Any staff member, not just managers: opening a container is the one
    // write a groom makes, and a menu that hides the page makes them find a
    // manager instead — which is how it stops being recorded at all.
    { section: 'Feed room', to: '/supplements', label: 'Supplements', show: isStaff },
    { section: 'Feed room', to: '/supplements/orders', label: 'Ordering', show: canManage },
    { section: 'Feed room', to: '/supplements/history', label: 'Inventory history', show: isStaff },

    { section: 'People', to: '/riders', label: 'Riders', show: canSchedule },
    { section: 'People', to: '/owners', label: 'Horse owners', show: canManage },
    { section: 'People', to: '/providers', label: 'Providers', show: isStaff },
    { section: 'People', to: '/team', label: 'Team', show: canManage },

    { section: 'Barn', to: '/settings/barn', label: 'Barn settings', show: canManage },
    { section: 'Barn', to: '/settings/archive', label: 'Archive', show: canManage },
  ].filter((i) => i.show);

  return (
    <div className="min-h-screen flex flex-col">
      <ViewAsBanner />

      {/* Top bar */}
      <header className="bg-white border-b border-ink-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* The lockup carries the name, so it takes the alt and the
                separate wordmark goes away — two of them side by side was one
                "stallSIDE" too many. */}
            <img src={logoUrl} alt="stallSIDE" className="h-11 sm:h-12 w-auto shrink-0" />
            {activeBarn && (
              <span className="text-sm text-ink-400 truncate">{activeBarn.name}</span>
            )}
          </div>
          <UserMenu />
        </div>

        {/* Desktop nav — forest band, mirrors the mobile bar. */}
        <nav className="hidden sm:block bg-[#1a2b1f]">
          <div className="max-w-7xl mx-auto px-2 flex items-stretch">
            <DeskTab to="/dashboard" icon={<HomeIcon />} label="Home" />
            <DeskTab to="/schedule" icon={<BoardIcon />} label="Board" />
            <DeskTab to="/horses" icon={<HorseHeadIcon />} label="Horses" />
            {isStaff && <DeskTab to="/schedule/shifts" icon={<RosterIcon />} label="Shifts" />}
            {canSchedule && (
              <DeskTab to="/schedule/availability" icon={<WeekIcon />} label="Availability" />
            )}
            {moreItems.length > 0 && (
              <NavMore items={moreItems} variant="desktop" icon={<MoreIcon />} />
            )}
          </div>
        </nav>
      </header>

      <main className="flex-1 pb-20 sm:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav — forest */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-[#1a2b1f] flex">
        <BottomTab to="/dashboard" icon={<HomeIcon />} label="Home" />
        <BottomTab to="/schedule" icon={<BoardIcon />} label="Board" />
        <BottomTab to="/horses" icon={<HorseHeadIcon />} label="Horses" />
        {isStaff && <BottomTab to="/schedule/shifts" icon={<RosterIcon />} label="Shifts" />}
        {canSchedule && (
          <BottomTab to="/schedule/availability" icon={<WeekIcon />} label="Availability" />
        )}
        {moreItems.length > 0 && (
          <NavMore items={moreItems} variant="bottom" icon={<MoreIcon />} />
        )}
      </nav>
    </div>
  );
}

/**
 * Icon only, with the name in the tooltip and the accessible name.
 *
 * A label under every glyph is fine at three tabs and starts to crowd at five,
 * and these four are the shapes somebody uses every day — they learn them once.
 * The aria-label stays either way: an icon-only link without one is a link a
 * screen reader announces as nothing.
 */
function DeskTab({ to, end, icon, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      title={label}
      className={({ isActive }) =>
        `flex items-center justify-center px-6 py-3 ${
          isActive
            ? 'text-[#f5f1e8] shadow-[inset_0_-2px_0_0_#f5f1e8]'
            : 'text-[#f5f1e8]/60 hover:text-[#f5f1e8]'
        }`
      }
    >
      {icon}
    </NavLink>
  );
}

function BottomTab({ to, end, icon, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      title={label}
      className={({ isActive }) =>
        // py-3 rather than py-2: dropping the label took height out of the
        // target, and this is tapped with a thumb in a barn.
        `flex-1 flex items-center justify-center py-3 ${
          isActive
            ? 'text-[#f5f1e8] shadow-[inset_0_2px_0_0_#f5f1e8]'
            : 'text-[#f5f1e8]/55'
        }`
      }
    >
      {icon}
    </NavLink>
  );
}

// --- icons (inline SVG, currentColor; swap for an icon lib later if desired) ---

/**
 * The daily board — a whiteboard on a barn wall, which is the thing this screen
 * replaced. The old week-grid icon moved to Availability, where a week grid is
 * literally what you're looking at.
 */
function BoardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden="true">
      {/* Board on an easel. The first attempt was a rectangle with a single
          stem, which at 20px read as a speech bubble — two splayed legs are
          what make it stand up rather than point at somebody. */}
      <rect x="3" y="3.5" width="18" height="12.5" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M9.5 16 8 21M14.5 16 16 21M7 7.5h10M7 11h6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v9h14v-9" />
    </svg>
  );
}

/**
 * The roster — who is working. Two figures rather than a calendar, because the
 * question this tab answers is "who", and the calendar glyph is already spoken
 * for by Availability sitting next to it.
 */
function RosterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20v-1.5A4.5 4.5 0 0 1 8 14h2a4.5 4.5 0 0 1 4.5 4.5V20" />
      <path d="M16.5 5.6a3.2 3.2 0 0 1 0 6.2M17 14.2a4.5 4.5 0 0 1 3.5 4.4V20" />
    </svg>
  );
}

function WeekIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
      <path d="M3.5 9h17M8 3v3M16 3v3" />
      <path d="M9 12.5v5M15 12.5v5" />
    </svg>
  );
}


function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" stroke="none">
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}
